import { fg } from '@atlaskit/platform-feature-flags';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { type ProductKeys } from '../../../../../common/constants/products';
import { type SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const products3PFromConfig = getProducts3PFromConfigForFilter('repository');

const FETCH_LIMIT = 30;

/**
 * Temporary hardcoded list of repositories. AGG's `thirdPartyFilterOptions`
 * endpoint does not yet expose a `repositories` filter group for GitHub. Once
 * it does, replace `fetchRepositoryOptions` with a real call (see QS-10140)
 * and delete this list.
 */
const HARDCODED_REPOSITORIES: ReadonlyArray<{ name: string; label?: string }> = [
	{ name: 'atlassian/atlassian-frontend-monorepo' },
	{ name: 'atlassian/design-system' },
	{ name: 'atlassian/rovo-search-connectors' },
	{ name: 'atlassian/jira-frontend' },
	{ name: 'atlassian/confluence-frontend' },
];

const toOptions = (
	repos: ReadonlyArray<{ name: string; label?: string | null }>,
	config: CloudConfig,
): SelectFilterOption[] =>
	repos.map((repo) => ({
		value: repo.name,
		trackingKey: repo.name,
		queryParamValue: repo.name,
		isDefault: false,
		label: repo.label ?? repo.name,
		product: config.product,
		isSelected: false,
		isSuggested: false,
	}));

/**
 * Returns the user's accessible GitHub repositories.
 */
const fetchRepositoryOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> =>
	toOptions(HARDCODED_REPOSITORIES, config);

const getRepositoryOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	if (fg('rovo_fps_show_repository_filter')) {
		return fetchRepositoryOptions(config);
	}
	return [];
};

/**
 * URL-hydration fetch. Resolves `?repository=...` values into
 * `SelectFilterOption`s by matching against the user's accessible repos.
 */
const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	if (queryParams.length === 0) {
		return [];
	}

	const allOptions = await getRepositoryOptions(config);
	const matched = allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);

	if (matched.length === 0 && fg('rovo_fps_show_repository_filter')) {
		return queryParams.map((queryParam) => ({
			value: queryParam,
			trackingKey: queryParam,
			queryParamValue: queryParam,
			isDefault: false,
			label: queryParam,
			product: config.product,
			isSelected: true,
			isSuggested: false,
		}));
	}

	return matched;
};

/**
 * Lookup (typeahead) fetcher.
 */
const fetchLookupOptions = async (
	input: string,
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	if (!fg('rovo_fps_show_repository_filter') || !input) {
		return [];
	}
	const allOptions = await getRepositoryOptions(config);
	const lowered = input.toLowerCase();
	return allOptions
		.filter((option) => option.label.toLowerCase().includes(lowered))
		.slice(0, FETCH_LIMIT);
};

export const filter: Readonly<{
	id: 'repository';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'repository'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'repository'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'repository'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'repository',
	products: products3PFromConfig,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'repository',
		fetcher: fetchQueryParams,
	},
	initialOptions: {
		fetcher: getRepositoryOptions,
	},
	lookup: {
		fetcher: fetchLookupOptions,
	},
});
